import { useContext, useEffect, useState } from "react";
import { Button, Message, SettingsCard, useToast } from "@humansignal/ui";
import { RadioGroup } from "../../components/Form/Elements/RadioGroup/RadioGroup";
import { ProjectContext } from "../../providers/ProjectProvider";
import { SettingsPageHeader } from "../../components/SettingsPageHeader/SettingsPageHeader";
import { spaNavigate } from "../../utils/navigation";
import { cn } from "../../utils/bem";
import { useTranslation } from "../../i18n/LocaleProvider";

/**
 * Project Settings → Visibility (crowdsource slice 6).
 *
 * Owner-facing surface for the fields that used to be settable only via
 * Django admin: who can reach the project and contribute to it.
 *
 *   - visibility          private | public
 *   - contribution_mode   members_only | open      (only when public)
 *   - max_contributors    integer | null (unlimited) (only when open)
 *
 * Plus a share link (copies /projects/<id>) and a read-only roster of
 * current ``ProjectContributor`` rows with a per-row Remove. The backend
 * gates every write to project managers (see
 * projects/serializers.py::validate_visibility and
 * projects/crowdsource_api.py::ProjectContributorsAPI); this page is
 * itself only reachable by managers (Settings is manager-gated), so the
 * UI mirrors that rather than re-deriving role here.
 */
const selectStyle = {
  height: 36,
  padding: "0 12px",
  borderRadius: 6,
  border: "1px solid var(--color-neutral-border, rgba(0,0,0,0.15))",
  background: "var(--color-neutral-background, #fff)",
  fontSize: "var(--font-size-body-small)",
  boxSizing: "border-box",
};

export const VisibilitySettings = () => {
  const { t } = useTranslation();
  const { project, updateProject } = useContext(ProjectContext);
  const toast = useToast();

  const [visibility, setVisibility] = useState("private");
  const [contributionMode, setContributionMode] = useState("members_only");
  // auto = join takes effect immediately; review = the owner approves each request.
  const [joinMode, setJoinMode] = useState("auto");
  // Kept as a string so the input can be blank ( = unlimited ).
  const [maxContributors, setMaxContributors] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // Org policies — enforced ones render locked ("Set by your
  // organization"); the server rejects/overrides regardless.
  const [orgPolicies, setOrgPolicies] = useState({});

  useEffect(() => {
    if (!project?.id) return;
    fetch(`/api/projects/${project.id}/effective-policies/`, {
      credentials: "same-origin",
      cache: "no-store",
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => setOrgPolicies(body?.policies ?? {}))
      .catch(() => {});
  }, [project?.id]);

  const enforced = (key) => orgPolicies[key]?.enforced && orgPolicies[key]?.value;
  const privateOnly =
    orgPolicies["visibility.allowed_visibilities"]?.enforced &&
    orgPolicies["visibility.allowed_visibilities"]?.value === "private_only";
  const joinLocked = enforced("visibility.join_approval_required");
  const capPolicy = orgPolicies["visibility.max_contributors_cap"];
  const orgCap = capPolicy?.enforced && capPolicy?.value > 0 ? capPolicy.value : null;

  // Seed local form state from the project once it resolves. Re-seed
  // when the project id changes (navigating between projects reuses
  // this component instance).
  useEffect(() => {
    if (!project?.id) return;
    setVisibility(project.visibility ?? "private");
    setContributionMode(project.contribution_mode ?? "members_only");
    setJoinMode(project.join_mode ?? "auto");
    setMaxContributors(project.max_contributors == null ? "" : String(project.max_contributors));
  }, [project?.id, project?.visibility, project?.contribution_mode, project?.join_mode, project?.max_contributors]);

  // The contributor roster itself now lives on the Members page under
  // the "Contributors" tab (see MembersSettings) — this section just
  // links there so there's one place to manage people.

  // ── Save ──────────────────────────────────────────────────────────
  const onSave = async () => {
    setError("");
    let maxParsed = null;
    const trimmed = maxContributors.trim();
    if (visibility === "public" && contributionMode === "open" && trimmed !== "") {
      const n = Number.parseInt(trimmed, 10);
      if (Number.isNaN(n) || n < 1) {
        setError("Contributor limit must be a whole number of 1 or more, or blank for unlimited.");
        return;
      }
      maxParsed = n;
    }
    setSaving(true);
    // Only send fields relevant to the chosen visibility so we don't
    // stamp a stale contribution_mode/limit onto a project the owner
    // just made private.
    const body = { visibility };
    if (visibility === "public") {
      body.contribution_mode = contributionMode;
      body.max_contributors = contributionMode === "open" ? maxParsed : null;
      if (contributionMode === "open") body.join_mode = joinMode;
    }
    // ``updateProject`` resolves with the API result (it does not throw)
    // and, called without ``returnErrors``, suppresses the global error
    // toast — so a failed save (e.g. the manager gate 403s a non-manager
    // who reached this by URL) is surfaced inline here instead.
    const result = await updateProject(body);
    setSaving(false);
    const ok = result && !result.error && result.$meta?.ok !== false && result.$meta;
    if (!ok) {
      const detail = result?.response?.detail || result?.error;
      setError(typeof detail === "string" ? detail : "Couldn't save visibility settings.");
      return;
    }
    toast.show({ message: "Visibility updated." });
  };

  // ── Share link ────────────────────────────────────────────────────
  const [copied, setCopied] = useState(false);
  const shareUrl =
    typeof window !== "undefined" && project?.id ? `${window.location.origin}/projects/${project.id}` : "";

  const onCopyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.show({ message: "Couldn't copy, select the link and copy manually.", type: "error" });
    }
  };

  if (!project) return null;

  return (
    <div className={cn("settings-page").toClassName()}>
      <SettingsPageHeader title="Visibility" subtitle="Control who can find this project and contribute to it." />

      <div className={cn("settings-page").elem("body").toClassName()}>
        {error ? (
          <Message variant="negative" size="small">
            {error}
          </Message>
        ) : null}

        <SettingsCard
          title="Who can see this project"
          description="Private projects are visible only to your organization. Public projects appear in Discover for any signed-in user."
        >
          {/* Selects with a one-line dynamic description (owner
              convention — same pattern as the HF Push card). Fields
              appear left-to-right as choices unlock; no indent stairs. */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 280px))",
              gap: 16,
              alignItems: "start",
            }}
          >
            <label
              style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: "var(--font-size-body-small)" }}
            >
              <span style={{ fontWeight: 600 }}>Visibility</span>
              <select
                value={visibility}
                onChange={(e) => setVisibility(e.target.value)}
                disabled={saving || privateOnly}
                style={selectStyle}
              >
                <option value="private">Private</option>
                <option value="public">Public</option>
              </select>
              <span style={{ color: "var(--color-neutral-content-subtler)", lineHeight: 1.4 }}>
                {privateOnly
                  ? "Private only, set by your organization."
                  : visibility === "private"
                    ? "Only members of your organization can see and open this project."
                    : "Listed in Discover. Anyone signed in can view it."}
              </span>
            </label>

            {visibility === "public" && (
              <label
                style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: "var(--font-size-body-small)" }}
              >
                <span style={{ fontWeight: 600 }}>Contribution</span>
                <select
                  value={contributionMode}
                  onChange={(e) => setContributionMode(e.target.value)}
                  disabled={saving}
                  style={selectStyle}
                >
                  <option value="members_only">Members only</option>
                  <option value="open">Open to anyone</option>
                </select>
                <span style={{ color: "var(--color-neutral-content-subtler)", lineHeight: 1.4 }}>
                  {contributionMode === "open"
                    ? "Any signed-in user can join and start annotating tasks."
                    : "Public to view, but only people you invite into the org can annotate."}
                </span>
              </label>
            )}

            {visibility === "public" && contributionMode === "open" && (
              <label
                style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: "var(--font-size-body-small)" }}
              >
                <span style={{ fontWeight: 600 }}>Join approval</span>
                <select
                  value={joinLocked ? "review" : joinMode}
                  onChange={(e) => setJoinMode(e.target.value)}
                  disabled={saving || joinLocked}
                  style={selectStyle}
                >
                  <option value="auto">
                    {t("pages.settings.visibility.join_auto_label", undefined, "Auto-approve")}
                  </option>
                  <option value="review">
                    {t("pages.settings.visibility.join_review_label", undefined, "Review requests first")}
                  </option>
                </select>
                <span style={{ color: "var(--color-neutral-content-subtler)", lineHeight: 1.4 }}>
                  {joinLocked
                    ? "Review-first, set by your organization."
                    : joinMode === "auto"
                      ? t(
                          "pages.settings.visibility.join_auto_desc",
                          undefined,
                          "Anyone who joins starts contributing right away.",
                        )
                      : t(
                          "pages.settings.visibility.join_review_desc",
                          undefined,
                          "Joining sends a request. Contributors get access only after you approve them on the Members page.",
                        )}
                </span>
              </label>
            )}

            {visibility === "public" && contributionMode === "open" && (
              <label
                style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: "var(--font-size-body-small)" }}
              >
                <span style={{ fontWeight: 600 }}>Maximum contributors</span>
                <input
                  type="number"
                  min="1"
                  max={orgCap ?? undefined}
                  inputMode="numeric"
                  placeholder={orgCap ? String(orgCap) : "Unlimited"}
                  value={maxContributors}
                  onChange={(e) => setMaxContributors(e.target.value)}
                  disabled={saving}
                  style={selectStyle}
                />
                <span style={{ color: "var(--color-neutral-content-subtler)", lineHeight: 1.4 }}>
                  {orgCap
                    ? `Capped at ${orgCap} by your organization.`
                    : "Leave blank for no limit. Lowering it never removes existing contributors."}
                </span>
              </label>
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
            <Button
              variant="primary"
              look="filled"
              size="small"
              style={{ width: 120 }}
              onClick={onSave}
              waiting={saving}
              disabled={saving}
            >
              Save
            </Button>
          </div>
        </SettingsCard>

        {visibility === "public" && (
          <SettingsCard
            title="Share link"
            description="Send this link to invite people to the project. Whether they can annotate depends on the contribution mode above."
          >
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="text"
                readOnly
                value={shareUrl}
                onFocus={(e) => e.target.select()}
                style={{
                  flex: "1 1 auto",
                  padding: "8px 12px",
                  border: "1px solid var(--color-neutral-border, rgba(0,0,0,0.15))",
                  borderRadius: 6,
                  background: "var(--color-neutral-surface, rgba(0,0,0,0.04))",
                  fontSize: "var(--font-size-body-small)",
                  fontFamily: "monospace",
                }}
              />
              <Button variant="neutral" look="outlined" size="small" onClick={onCopyShare}>
                {copied ? "Copied" : "Copy link"}
              </Button>
            </div>
          </SettingsCard>
        )}

        {visibility === "public" && contributionMode === "open" && (
          <SettingsCard
            title="Contributors"
            description="People who joined this project through Discover are managed on the Members page, under the Contributors tab."
          >
            <Button
              variant="neutral"
              look="outlined"
              size="small"
              onClick={() => spaNavigate(`/projects/${project.id}/members`)}
            >
              Manage contributors
            </Button>
          </SettingsCard>
        )}
      </div>
    </div>
  );
};

VisibilitySettings.menuItem = "Visibility";
VisibilitySettings.title = "Visibility";
VisibilitySettings.path = "/visibility";
VisibilitySettings.exact = true;

export default VisibilitySettings;
